import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faSpinner, 
  faUser, 
  faCalendarAlt, 
  faFileAlt, 
  faClock, 
  faCheckCircle, 
  faExclamationTriangle,
  faBuilding,
  faClipboardList,
  faPercent,
  faStickyNote,
  faHistory,
  faPlay,
  faPause,
  faCheck,
  faArrowRight,
  faEye,
  faEdit
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { getSubmission } from '../../services/submissionApi';
import StatusBadge from './StatusBadge';

interface SubmissionDetailModalProps {
  submissionId: string;
  onClose: () => void;
  onUpdate: () => void;
}

const SubmissionDetailModal: React.FC<SubmissionDetailModalProps> = ({
  submissionId,
  onClose,
  onUpdate
}) => {
  const [submission, setSubmission] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSubmission = async () => {
      try {
        setLoading(true);
        const response = await getSubmission(submissionId);
        setSubmission(response.data);
      } catch (err: any) {
        console.error('Error loading submission:', err);
        setError(err.message || 'Fehler beim Laden der Submission');
      } finally {
        setLoading(false);
      }
    };

    if (submissionId) {
      loadSubmission();
    }
  }, [submissionId]);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Nicht gesetzt';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateString: string) => {
    if (!dateString) return 'Nicht gesetzt';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getDaysUntilDeadline = (deadline: string) => {
    if (!deadline) return 0;
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // **STATUS VERLAUF ICONS**
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft': return faEdit;
      case 'sent': return faPlay;
      case 'in_progress': return faPause;
      case 'completed': return faCheck;
      case 'overdue': return faExclamationTriangle;
      default: return faEye;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'draft': return 'Entwurf';
      case 'sent': return 'Versendet';
      case 'in_progress': return 'In Bearbeitung';
      case 'completed': return 'Abgeschlossen';
      case 'overdue': return 'Überfällig';
      default: return 'Unbekannt';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-700 border-gray-300';
      case 'sent': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'in_progress': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'completed': return 'bg-green-100 text-green-700 border-green-300';
      case 'overdue': return 'bg-red-100 text-red-700 border-red-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50"
      >
        <div className="bg-white rounded-xl p-8 shadow-2xl">
          <div className="text-center">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-blue-500 mb-4" />
            <p className="text-gray-600">Lade Submission-Details...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  if (error || !submission) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
          <div className="text-center">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 text-4xl mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Fehler</h3>
            <p className="text-gray-600 mb-6">{error || 'Submission konnte nicht geladen werden'}</p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Schließen
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  const daysUntil = getDaysUntilDeadline(submission.deadline);

  // **ECHTE STATUS HISTORY GENERIEREN**
  const generateStatusHistory = (submission: any) => {
    const history = [];
    
    // 1. CREATED
    history.push({
      status: 'draft',
      changedAt: submission.createdAt,
      changedBy: submission.createdBy || 'System',
      reason: 'Erstellt via Bulk-Send',
      automatic: false
    });

    // 2. SENT (wenn sentAt vorhanden)
    if (submission.sentAt) {
      history.push({
        status: 'sent',
        changedAt: submission.sentAt,
        changedBy: submission.sentBy || 'System',
        reason: 'E-Mail an Lieferant versendet',
        automatic: true
      });
    }

    // 3. IN_PROGRESS (wenn erste Antwort vorhanden)
    if (submission.answers && submission.answers.length > 0) {
      const firstAnswer = submission.answers[0];
      history.push({
        status: 'in_progress',
        changedAt: firstAnswer.answeredAt || submission.updatedAt,
        changedBy: submission.supplierId?.name || 'Lieferant',
        reason: 'Erste Antwort eingegangen',
        automatic: true
      });
    }

    // 4. COMPLETED (wenn completedAt vorhanden)
    if (submission.completedAt) {
      history.push({
        status: 'completed',
        changedAt: submission.completedAt,
        changedBy: submission.supplierId?.name || 'Lieferant',
        reason: 'Fragebogen vollständig ausgefüllt',
        automatic: true
      });
    }

    // 5. OVERDUE (wenn überfällig)
    if (submission.status === 'overdue' || daysUntil < 0) {
      history.push({
        status: 'overdue',
        changedAt: submission.deadline,
        changedBy: 'System',
        reason: 'Deadline überschritten',
        automatic: true
      });
    }

    // Existing statusHistory hinzufügen
    if (submission.statusHistory && submission.statusHistory.length > 0) {
      submission.statusHistory.forEach((status: any) => {
        history.push({
          status: status.status,
          changedAt: status.changedAt,
          changedBy: status.changedBy || 'Unbekannt',
          reason: status.reason || '',
          automatic: status.automatic || false
        });
      });
    }

    // Nach Datum sortieren - **NEUESTE ZUERST**
    return history.sort((a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime());
  };

  const statusHistory = generateStatusHistory(submission);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* **KOMPAKTER HEADER IN WEDL GRAU** */}
          <div className="bg-gray-800 text-white px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-white/10 p-2 rounded">
                  <FontAwesomeIcon icon={faFileAlt} className="text-lg" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold">Submission Details</h1>
                  <div className="flex items-center space-x-4 mt-1 text-sm">
                    <div className="flex items-center space-x-1">
                      <FontAwesomeIcon icon={faBuilding} className="text-gray-300" />
                      <span className="text-gray-300">{submission.supplierId?.name || 'Unbekannter Lieferant'}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <FontAwesomeIcon icon={faClipboardList} className="text-gray-300" />
                      <span className="text-gray-300">{submission.questionnaireId?.title?.de || 'Unbekannter Fragebogen'}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={onClose}
                  className="text-white/80 hover:text-white hover:bg-white/10 rounded p-1.5 transition-all"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-lg" />
                </button>
              </div>
            </div>
          </div>

          {/* **KOMPAKTER CONTENT** */}
          <div className="flex-1 overflow-y-auto max-h-[calc(95vh-120px)] p-4">
            
            {/* **FORTSCHRITT GANZ OBEN** */}
            <div className="mb-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-3 border border-purple-200 shadow-sm">
              <div className="flex items-center space-x-2 mb-3">
                <FontAwesomeIcon icon={faPercent} className="text-purple-500 text-sm" />
                <h3 className="text-sm font-bold text-purple-900">Fortschritt</h3>
              </div>
              
              <div className="bg-white rounded p-3 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-700">
                    {submission.answeredQuestions || 0} von {submission.totalQuestions || 0} Fragen beantwortet
                  </span>
                  <span className="text-sm font-bold text-purple-600">
                    {submission.progressPercentage || 0}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-500 shadow-sm ${
                      (submission.progressPercentage || 0) >= 80 ? 'bg-gradient-to-r from-green-500 to-green-600' :
                      (submission.progressPercentage || 0) >= 50 ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' :
                      (submission.progressPercentage || 0) >= 20 ? 'bg-gradient-to-r from-orange-500 to-orange-600' : 
                      'bg-gradient-to-r from-red-500 to-red-600'
                    }`}
                    style={{ width: `${submission.progressPercentage || 0}%` }}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              
              {/* **KOMPAKTE GRUNDINFORMATIONEN - OHNE HINTERGRUND BEI ICON** */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 border border-blue-200 shadow-sm">
                <div className="flex items-center space-x-2 mb-3">
                  <FontAwesomeIcon icon={faUser} className="text-blue-500 text-sm" />
                  <h3 className="text-sm font-bold text-blue-900">Grundinformationen</h3>
                </div>
                
                <div className="space-y-2">
                  <div className="bg-white rounded p-2 shadow-sm">
                    <div className="text-xs font-medium text-blue-600 mb-1">Lieferant</div>
                    <div className="text-xs font-semibold text-gray-900">
                      {submission.supplierId?.name || 'Unbekannt'}
                    </div>
                    {submission.supplierId?.email && (
                      <div className="text-xs text-gray-500 mt-0.5">{submission.supplierId.email}</div>
                    )}
                  </div>

                  <div className="bg-white rounded p-2 shadow-sm">
                    <div className="text-xs font-medium text-blue-600 mb-1">Fragebogen</div>
                    <div className="text-xs font-semibold text-gray-900">
                      {submission.questionnaireId?.title?.de || 'Unbekannt'}
                    </div>
                    {submission.questionnaireId?.version && (
                      <div className="text-xs text-gray-500 mt-0.5">Version {submission.questionnaireId.version}</div>
                    )}
                  </div>

                  <div className="bg-white rounded p-2 shadow-sm">
                    <div className="text-xs font-medium text-blue-600 mb-1">Status</div>
                    <StatusBadge status={submission.status} deadline={submission.deadline} />
                  </div>
                </div>
              </div>

              {/* **KOMPAKTE ZEITSTEMPEL - OHNE HINTERGRUND BEI ICON** */}
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-3 border border-green-200 shadow-sm">
                <div className="flex items-center space-x-2 mb-3">
                  <FontAwesomeIcon icon={faCalendarAlt} className="text-green-500 text-sm" />
                  <h3 className="text-sm font-bold text-green-900">Zeitstempel</h3>
                </div>
                
                <div className="space-y-2">
                  <div className="bg-white rounded p-2 shadow-sm">
                    <div className="text-xs font-medium text-green-600 mb-1">Erstellt</div>
                    <div className="text-xs font-semibold text-gray-900">
                      {formatDate(submission.createdAt)}
                    </div>
                  </div>

                  {submission.sentAt && (
                    <div className="bg-white rounded p-2 shadow-sm">
                      <div className="text-xs font-medium text-green-600 mb-1">Versendet</div>
                      <div className="text-xs font-semibold text-gray-900">
                        {formatDate(submission.sentAt)}
                      </div>
                    </div>
                  )}

                  <div className="bg-white rounded p-2 shadow-sm">
                    <div className="text-xs font-medium text-green-600 mb-1">Deadline</div>
                    <div className="text-xs font-semibold text-gray-900">
                      {formatDate(submission.deadline)}
                    </div>
                    <div className={`text-xs mt-1 font-medium ${
                      daysUntil < 0 ? 'text-red-600' : 
                      daysUntil <= 2 ? 'text-orange-600' : 'text-green-600'
                    }`}>
                      {daysUntil < 0 
                        ? `${Math.abs(daysUntil)} Tag${Math.abs(daysUntil) !== 1 ? 'e' : ''} überfällig`
                        : `${daysUntil} Tag${daysUntil !== 1 ? 'e' : ''} verbleibend`
                      }
                    </div>
                  </div>

                  {submission.completedAt && (
                    <div className="bg-white rounded p-2 shadow-sm">
                      <div className="text-xs font-medium text-green-600 mb-1">Abgeschlossen</div>
                      <div className="text-xs font-semibold text-green-700">
                        {formatDate(submission.completedAt)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* **KOMPAKTE INTERNE NOTIZEN - OHNE HINTERGRUND BEI ICON** */}
            {submission.internalNotes && submission.internalNotes.length > 0 && (
              <div className="mt-4 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-3 border border-yellow-200 shadow-sm">
                <div className="flex items-center space-x-2 mb-3">
                  <FontAwesomeIcon icon={faStickyNote} className="text-yellow-500 text-sm" />
                  <h3 className="text-sm font-bold text-yellow-900">Interne Notizen</h3>
                </div>
                
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {submission.internalNotes.map((note: any, index: number) => (
                    <div key={index} className="bg-white border border-yellow-200 rounded p-2 shadow-sm">
                      <div className="text-xs text-gray-900 mb-1">{note.note}</div>
                      <div className="text-xs text-gray-500">
                        {note.createdBy} • {formatDate(note.createdAt)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* **KOMPAKTER STATUS-VERLAUF - OHNE HINTERGRUND BEI ICON & NEUESTE ZUERST** */}
            <div className="mt-4 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-3 border border-indigo-200 shadow-sm">
              <div className="flex items-center space-x-2 mb-3">
                <FontAwesomeIcon icon={faHistory} className="text-indigo-500 text-sm" />
                <h3 className="text-sm font-bold text-indigo-900">Status-Verlauf</h3>
              </div>
              
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {statusHistory.map((status: any, index: number) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white border border-indigo-200 rounded p-3 shadow-sm relative"
                  >
                    {/* Connector Line */}
                    {index < statusHistory.length - 1 && (
                      <div className="absolute left-4 top-8 w-0.5 h-6 bg-indigo-200"></div>
                    )}
                    
                    <div className="flex items-start space-x-3">
                      <div className={`flex items-center justify-center w-6 h-6 rounded-full ${getStatusColor(status.status)}`}>
                        <FontAwesomeIcon icon={getStatusIcon(status.status)} className="text-xs" />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${getStatusColor(status.status)}`}>
                              {getStatusText(status.status)}
                            </span>
                            {status.reason && (
                              <>
                                <FontAwesomeIcon icon={faArrowRight} className="text-indigo-400 text-xs" />
                                <span className="text-xs text-gray-600">{status.reason}</span>
                              </>
                            )}
                            {status.automatic && (
                              <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-600 rounded text-xs">
                                Auto
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 font-medium">
                            {formatDate(status.changedAt)}
                          </div>
                        </div>
                        
                        <div className="text-xs text-gray-500">
                          Geändert von: <span className="font-medium">{status.changedBy}</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              {statusHistory.length === 0 && (
                <div className="bg-white border border-indigo-200 rounded p-3 text-center">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-indigo-400 text-lg mb-2" />
                  <div className="text-xs text-gray-600">Kein Status-Verlauf verfügbar</div>
                </div>
              )}
            </div>
          </div>

          {/* **KOMPAKTER FOOTER** */}
          <div className="border-t bg-gray-50 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-3 text-xs text-gray-600">
              <span className="font-mono bg-gray-200 px-2 py-1 rounded text-xs">ID: {submission._id}</span>
              <span>•</span>
              <span>Erstellt am {formatDateShort(submission.createdAt)}</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={onClose}
                className="px-3 py-1.5 bg-gray-500 text-white rounded text-sm hover:bg-gray-600 transition-all"
              >
                Schließen
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SubmissionDetailModal;